/**
 * Free Gift Popup
 * ---------------
 * Opens a modal when the cart subtotal (excluding gift lines) reaches a threshold
 * and lets the customer add one free gift. The gift line is tagged with the
 * line item property `_free_gift`. Works with Minimog's MinimogTheme.Cart and
 * MinimogEvents bus. Pricing is enforced by a Shopify automatic BXGY discount.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-free-gift-popup]');
  if (!root) return;

  var configNode = root.querySelector('[data-fgp-config]');
  var config;
  try {
    config = JSON.parse(configNode.textContent);
  } catch (err) {
    console.error('[free-gift-popup] Invalid config', err);
    return;
  }

  var PROP = config.property || '_free_gift';
  var STORAGE_KEY = 'fgp:dismissed';
  var state = {
    cart: null,
    busy: false,
    open: false,
    lastFocus: null,
    hasShownRemovedNotice: false
  };

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */

  function routes() {
    var r = (window.MinimogSettings && window.MinimogSettings.routes) || {};
    var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return {
      root: rootUrl,
      add: r.cart_add_url || rootUrl + 'cart/add',
      change: r.cart_change_url || rootUrl + 'cart/change',
      cartJson: rootUrl + 'cart.js'
    };
  }

  function fetchJSON(url, options) {
    options = options || {};
    options.headers = Object.assign(
      { 'Content-Type': 'application/json', Accept: 'application/json' },
      options.headers || {}
    );
    return fetch(url, options).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var e = new Error(data && (data.description || data.message) || 'Request failed');
          e.status = res.status;
          e.data = data;
          throw e;
        }
        return data;
      });
    });
  }

  function formatMoney(cents) {
    var format = (window.MinimogSettings && window.MinimogSettings.money_format) || '${{amount}}';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(cents, format);
      } catch (err) { /* fall through */ }
    }
    var amount = (cents / 100).toFixed(2);
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, amount);
  }

  function isGiftItem(item) {
    return !!(item && item.properties && item.properties[PROP]);
  }

  function giftLines(cart) {
    return (cart && cart.items ? cart.items : []).filter(isGiftItem);
  }

  function thresholdInCartCurrency() {
    var rate = 1;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
      var r = parseFloat(window.Shopify.currency.rate);
      if (!isNaN(r) && r > 0) rate = r;
    }
    return Math.round(config.threshold * rate);
  }

  function eligibleSubtotal(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, item) {
      if (isGiftItem(item)) return sum;
      return sum + (typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price || 0);
    }, 0);
  }

  function isEligible(cart) {
    return eligibleSubtotal(cart) >= thresholdInCartCurrency();
  }

  function dismissed() {
    try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch (e) { return false; }
  }
  function setDismissed(val) {
    try {
      if (val) sessionStorage.setItem(STORAGE_KEY, '1');
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* ignore */ }
  }

  function themeCart() {
    return window.MinimogTheme && window.MinimogTheme.Cart;
  }

  /** UpCart (cart drawer app) is present when its public API is defined. */
  function hasUpcart() {
    return typeof window.upcartSubscribeCartUpdated === 'function';
  }

  function notify(message, type) {
    var Cart = themeCart();
    var Notification = window.MinimogTheme && window.MinimogTheme.Notification;
    var target = Cart && Cart.domNodes && (Cart.domNodes.cartDrawerItems || Cart.domNodes.cartDrawerContent);
    if (Notification && typeof Notification.show === 'function' && target) {
      try {
        Notification.show({ target: target, method: 'prepend', type: type || 'success', message: message, delay: 300 });
        return;
      } catch (err) { /* fall through */ }
    }
    console.info('[free-gift-popup] ' + message);
  }

  /** Re-render whichever cart drawer is in use and broadcast the new cart. */
  function syncTheme(cart, openDrawer) {
    var Cart = themeCart();
    if (Cart) Cart.cart = cart;

    if (hasUpcart()) {
      var p = Promise.resolve();
      if (typeof window.upcartRefreshCart === 'function') {
        p = Promise.resolve(window.upcartRefreshCart()).catch(function (err) {
          console.warn('[free-gift-popup] upcartRefreshCart failed', err);
        });
      }
      return p.then(function () {
        if (openDrawer && typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
        // Keep the theme header count in sync (theme subscribes to this event).
        if (window.MinimogEvents) window.MinimogEvents.emit('ON_CART_UPDATE', cart);
      });
    }

    if (!Cart) return Promise.resolve();
    var q = Promise.resolve();
    if (typeof Cart.renderNewCart === 'function') {
      q = q.then(function () { return Cart.renderNewCart(); }).catch(function (err) {
        console.warn('[free-gift-popup] renderNewCart failed', err);
      });
    }
    return q.then(function () {
      if (openDrawer && typeof Cart.openCartDrawer === 'function') Cart.openCartDrawer();
      if (window.MinimogEvents) window.MinimogEvents.emit('ON_CART_UPDATE', cart);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Popup UI                                                           */
  /* ------------------------------------------------------------------ */

  var errorNode = root.querySelector('[data-fgp-error]');

  function showError(msg) {
    if (!errorNode) return;
    errorNode.textContent = msg || config.strings.error;
    errorNode.classList.add('is-visible');
  }
  function clearError() {
    if (!errorNode) return;
    errorNode.textContent = '';
    errorNode.classList.remove('is-visible');
  }

  function openPopup() {
    if (state.open) return;
    state.open = true;
    state.lastFocus = document.activeElement;
    clearError();
    root.classList.add('fgp--open');
    root.setAttribute('aria-hidden', 'false');
    if (fab) fab.classList.remove('is-visible');
    document.documentElement.classList.add('fgp-lock');
    document.body.style.overflow = 'hidden';
    setTimeout(function () {
      root.classList.add('fgp--visible');
      var closeBtn = root.querySelector('.fgp__close');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }, 20);
    document.addEventListener('keydown', onKeydown);
  }

  function closePopup(opts) {
    opts = opts || {};
    if (!state.open) return;
    if (opts.user && config.requireSelection) return;
    state.open = false;
    root.classList.remove('fgp--visible');
    document.removeEventListener('keydown', onKeydown);
    setTimeout(function () {
      root.classList.remove('fgp--open');
      root.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('fgp-lock');
      document.body.style.overflow = '';
      if (state.lastFocus && typeof state.lastFocus.focus === 'function') {
        try { state.lastFocus.focus(); } catch (e) { /* ignore */ }
      }
      renderFab(state.cart);
    }, 250);
    if (opts.dismiss) setDismissed(true);
  }

  function onKeydown(e) {
    if (e.key === 'Escape') closePopup({ dismiss: true, user: true });
  }

  root.addEventListener('click', function (e) {
    var closeTrigger = e.target.closest('[data-fgp-close]');
    if (closeTrigger) {
      e.preventDefault();
      closePopup({ dismiss: true, user: true });
      return;
    }
    var addBtn = e.target.closest('[data-fgp-add]');
    if (addBtn) {
      e.preventDefault();
      var item = addBtn.closest('[data-fgp-item]');
      var variantInput = item && item.querySelector('[data-fgp-variant]');
      var variantId = variantInput && parseInt(variantInput.value, 10);
      if (!variantId) {
        showError();
        return;
      }
      addGift(variantId, addBtn);
    }
  });

  /* ------------------------------------------------------------------ */
  /* Cart bar (inside the drawer / cart page)                           */
  /* ------------------------------------------------------------------ */

  function renderBar(cart) {
    renderFab(cart);
    var bars = document.querySelectorAll('[data-free-gift-bar]');
    if (!bars.length) return;
    var gifts = giftLines(cart);
    var eligible = isEligible(cart);
    var html = '';
    var cls = 'fgp-bar';

    if (eligible && gifts.length) {
      cls += ' fgp-bar--added is-visible';
      html =
        '<span class="fgp-bar__icon" aria-hidden="true">🎁</span>' +
        '<span class="fgp-bar__text">' + escapeHTML(config.strings.barAdded) + '</span>' +
        '<button type="button" class="fgp-bar__action" data-fgp-open>' + escapeHTML(config.strings.barChange) + '</button>';
    } else if (eligible) {
      cls += ' fgp-bar--unlocked is-visible';
      html =
        '<span class="fgp-bar__icon" aria-hidden="true">🎁</span>' +
        '<span class="fgp-bar__text">' + escapeHTML(config.strings.barUnlocked) + '</span>' +
        '<button type="button" class="fgp-bar__action" data-fgp-open>' + escapeHTML(config.strings.barChoose) + '</button>';
    } else if (config.showProgress && cart && cart.item_count > 0) {
      var remaining = thresholdInCartCurrency() - eligibleSubtotal(cart);
      cls += ' fgp-bar--progress is-visible';
      html =
        '<span class="fgp-bar__icon" aria-hidden="true">🎁</span>' +
        '<span class="fgp-bar__text">' +
        escapeHTML(config.strings.barProgress).replace('[amount]', '<strong>' + formatMoney(remaining) + '</strong>') +
        '</span>';
    }

    Array.prototype.forEach.call(bars, function (bar) {
      bar.className = cls;
      bar.innerHTML = html;
    });
  }

  /** Floating pill shown when a gift is unlocked but not yet chosen. */
  var fab = document.querySelector('[data-fgp-fab]');
  function renderFab(cart) {
    if (!fab) return;
    var show = config.showFab && cart && isEligible(cart) && !giftLines(cart).length && !state.open;
    fab.classList.toggle('is-visible', !!show);
  }

  function escapeHTML(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-fgp-open]');
    if (!trigger) return;
    e.preventDefault();
    setDismissed(false);
    openPopup();
  });

  /* ------------------------------------------------------------------ */
  /* Cart mutations                                                     */
  /* ------------------------------------------------------------------ */

  function addGift(variantId, button) {
    if (state.busy) return;
    state.busy = true;
    clearError();
    if (button) button.classList.add('is-loading');
    var r = routes();
    var existing = giftLines(state.cart);

    // Remove any existing gift first (handles "Change" flow), then add the new one.
    var chain = Promise.resolve();
    existing.forEach(function (line) {
      chain = chain.then(function () {
        return fetchJSON(r.change, { method: 'POST', body: JSON.stringify({ id: line.key, quantity: 0 }) });
      });
    });

    var properties = {};
    properties[PROP] = config.propertyValue || 'Free gift';

    chain
      .then(function () {
        return fetchJSON(r.add, {
          method: 'POST',
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: properties }] })
        });
      })
      .then(function () { return fetchJSON(r.cartJson); })
      .then(function (cart) {
        state.cart = cart;
        setDismissed(false);
        closePopup();
        return syncTheme(cart, true).then(function () {
          renderBar(cart);
          notify(config.strings.added, 'success');
        });
      })
      .catch(function (err) {
        console.warn('[free-gift-popup] add failed', err);
        showError(err && err.message && err.status === 422 ? err.message : config.strings.error);
      })
      .then(function () {
        state.busy = false;
        if (button) button.classList.remove('is-loading');
        scheduleEvaluate(false);
      });
  }

  function removeGifts(cart, reason) {
    var lines = giftLines(cart);
    if (!lines.length) return Promise.resolve(cart);
    if (state.busy) return Promise.resolve(cart);
    state.busy = true;
    var r = routes();
    var chain = Promise.resolve();
    lines.forEach(function (line) {
      chain = chain.then(function () {
        return fetchJSON(r.change, { method: 'POST', body: JSON.stringify({ id: line.key, quantity: 0 }) });
      });
    });
    return chain
      .then(function () { return fetchJSON(r.cartJson); })
      .then(function (newCart) {
        state.cart = newCart;
        state.busy = false;
        return syncTheme(newCart, false).then(function () {
          if (reason === 'below-threshold') notify(config.strings.removed, 'warning');
          return newCart;
        });
      })
      .catch(function (err) {
        state.busy = false;
        console.warn('[free-gift-popup] remove failed', err);
        return cart;
      });
  }

  function capGiftQuantity(cart) {
    var over = giftLines(cart).filter(function (l) { return l.quantity > 1; });
    if (!over.length || state.busy) return Promise.resolve(cart);
    state.busy = true;
    var r = routes();
    var chain = Promise.resolve();
    over.forEach(function (line) {
      chain = chain.then(function () {
        return fetchJSON(r.change, { method: 'POST', body: JSON.stringify({ id: line.key, quantity: 1 }) });
      });
    });
    return chain
      .then(function () { return fetchJSON(r.cartJson); })
      .then(function (newCart) {
        state.cart = newCart;
        state.busy = false;
        return syncTheme(newCart, false).then(function () { return newCart; });
      })
      .catch(function (err) {
        state.busy = false;
        console.warn('[free-gift-popup] cap qty failed', err);
        return cart;
      });
  }

  /* ------------------------------------------------------------------ */
  /* Core evaluation                                                    */
  /* ------------------------------------------------------------------ */

  function evaluate(cart, options) {
    options = options || {};
    if (!cart || !cart.items) return;
    state.cart = cart;

    var eligible = isEligible(cart);
    var gifts = giftLines(cart);

    if (eligible) {
      if (gifts.length) {
        if (state.open && !options.keepOpen) closePopup();
        capGiftQuantity(cart).then(renderBar);
        return;
      }
      renderBar(cart);
      if (options.allowPopup && !dismissed() && !state.open) openPopup();
      return;
    }

    // Not eligible
    setDismissed(false);
    if (state.open) closePopup();
    if (gifts.length && config.autoRemove) {
      removeGifts(cart, 'below-threshold').then(renderBar);
      return;
    }
    renderBar(cart);
  }

  /* ------------------------------------------------------------------ */
  /* Cart change detection                                              */
  /* ------------------------------------------------------------------ */

  var refreshTimer = null;
  function scheduleEvaluate(allowPopup) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      if (state.busy) return;
      fetchJSON(routes().cartJson).then(function (cart) {
        evaluate(cart, { allowPopup: allowPopup });
      }).catch(function (err) {
        console.warn('[free-gift-popup] cart fetch failed', err);
      });
    }, 200);
  }

  /**
   * Watch every cart mutation on the page (theme, UpCart, other apps) by
   * wrapping fetch and XMLHttpRequest. Cart drawer apps only broadcast their
   * own changes, so this is the reliable signal.
   */
  var CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
  function installInterceptors() {
    if (window.__fgpInterceptorsInstalled) return;
    window.__fgpInterceptorsInstalled = true;

    var origFetch = window.fetch;
    if (typeof origFetch === 'function') {
      window.fetch = function () {
        var args = arguments;
        var p = origFetch.apply(this, args);
        try {
          var input = args[0];
          var url = typeof input === 'string' ? input : (input && input.url) || '';
          if (CART_MUTATION_RE.test(url)) {
            p.then(function (res) {
              if (res && res.ok && !state.busy) scheduleEvaluate(true);
            }, function () { /* ignore */ });
          }
        } catch (e) { /* ignore */ }
        return p;
      };
    }

    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      try {
        if (CART_MUTATION_RE.test(String(url))) {
          this.addEventListener('load', function () {
            if (this.status >= 200 && this.status < 300 && !state.busy) scheduleEvaluate(true);
          });
        }
      } catch (e) { /* ignore */ }
      return origOpen.apply(this, arguments);
    };
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                               */
  /* ------------------------------------------------------------------ */

  var booted = false;
  function boot() {
    if (booted) return;
    booted = true;

    installInterceptors();

    if (hasUpcart()) {
      // UpCart drawer app: it intercepts add-to-cart and emits its own events.
      window.upcartSubscribeCartUpdated(function () { scheduleEvaluate(true); });
      if (typeof window.upcartSubscribeAddedToCart === 'function') {
        window.upcartSubscribeAddedToCart(function () { scheduleEvaluate(true); });
      }
      if (typeof window.upcartSubscribeItemRemoved === 'function') {
        window.upcartSubscribeItemRemoved(function () { scheduleEvaluate(true); });
      }
      if (typeof window.upcartSubscribeCartLoaded === 'function') {
        window.upcartSubscribeCartLoaded(function () { scheduleEvaluate(!!config.showOnLoad); });
      }
      if (typeof window.upcartSubscribeCartOpened === 'function') {
        window.upcartSubscribeCartOpened(function () { renderBar(state.cart); });
      }
    } else if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function') {
      window.MinimogEvents.subscribe('ON_CART_UPDATE', function (cart) {
        // Ignore updates we triggered ourselves while busy to avoid loops.
        if (state.busy) return;
        evaluate(cart, { allowPopup: true });
      });
      window.MinimogEvents.subscribe('ON_OPEN_DRAWER_CART', function (cart) {
        if (state.busy) return;
        renderBar(cart || state.cart);
      });
    }

    var initial = fetchJSON(routes().cartJson);
    initial.then(function (cart) {
      evaluate(cart, { allowPopup: !!config.showOnLoad });
    }).catch(function (err) {
      console.warn('[free-gift-popup] initial cart fetch failed', err);
    });
  }

  // Wait for the theme's Cart + event bus (app.min.js is loaded async).
  var tries = 0;
  (function waitForTheme() {
    var Cart = themeCart();
    var themeReady = !!(window.MinimogEvents && Cart && Cart.cart && Cart.cart.items && Cart.domNodes);
    // Give UpCart a moment to define its API even if the theme cart is ready first.
    if (hasUpcart() || (themeReady && tries >= 5)) {
      boot();
      return;
    }
    tries += 1;
    if (tries > 100) { // ~20s: theme JS not present (e.g. PageSpeed bot). Boot with what we have.
      boot();
      return;
    }
    setTimeout(waitForTheme, 200);
  })();

  // Small public handle for debugging / other scripts.
  window.freeGiftPopup = {
    open: function () { setDismissed(false); openPopup(); },
    close: function () { closePopup(); },
    refresh: function () { scheduleEvaluate(true); },
    getState: function () { return { cart: state.cart, busy: state.busy, open: state.open, eligible: isEligible(state.cart), gifts: giftLines(state.cart).length }; }
  };

  // Theme editor: re-evaluate when this section is (re)loaded or selected.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target && e.target.contains(root)) {
        setDismissed(false);
        openPopup();
      }
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target && e.target.contains(root)) closePopup();
    });
  }
})();
